import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import type { TechnicalAttribute } from '../../types'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion'

const NL_GROUP: Record<string, string> = {
  General: 'Algemeen',
  Memory: 'Geheugen',
  Processor: 'Processor',
  Storage: 'Opslag',
  Display: 'Beeldscherm',
  Interfaces: 'Aansluitingen',
  Communication: 'Communicatie',
  Power: 'Voeding',
  Certifications: 'Certificeringen',
  Environmental: 'Omgevingscondities',
  Mechanical: 'Mechanisch',
  Software: 'Software',
  Expansion: 'Uitbreiding',
  Safety: 'Veiligheid',
  Performance: 'Prestaties',
  Network: 'Netwerk',
  'Key Features': 'Kernfuncties',
}

// Dutch translations for common B&R catalog spec keys.
// Lookup is normalised (lowercase, underscores→spaces) so both raw API keys
// ("NOMINAL VOLTAGE") and formatted keys ("Nominal Voltage") resolve correctly.
const NL_SPEC_LABEL: Record<string, string> = {
  // Electrical
  'nominal voltage': 'Nominale spanning',
  'nominal current': 'Nominale stroom',
  'input voltage': 'Ingangsspanning',
  'output voltage': 'Uitgangsspanning',
  'supply voltage': 'Voedingsspanning',
  'rated voltage': 'Nominale spanning',
  'rated current': 'Nominale stroom',
  'max current': 'Max. stroom',
  'power consumption': 'Vermogensopname',
  'power dissipation': 'Warmteverliezen',
  'current consumption': 'Stroomverbruik',
  // Indicators / controls
  'leds': "LED's",
  'led': 'LED',
  'buzzer': 'Zoemer',
  'power button': 'Aan/uit-knop',
  'reset button': 'Resetknop',
  'battery': 'Batterij',
  // Thermal / mechanical
  'cooling': 'Koeling',
  'operating temperature': 'Bedrijfstemperatuur',
  'storage temperature': 'Opslagtemperatuur',
  'ambient temperature': 'Omgevingstemperatuur',
  'protection class': 'Beschermingsklasse',
  'ip rating': 'IP-klasse',
  'mounting': 'Montage',
  'weight': 'Gewicht',
  'dimensions': 'Afmetingen',
  'height': 'Hoogte',
  'width': 'Breedte',
  'depth': 'Diepte',
  'housing': 'Behuizing',
  // Software / firmware
  'bootloader': 'Bootloader',
  'operating system': 'Besturingssysteem',
  'firmware': 'Firmware',
  'software': 'Software',
  // Connectivity
  'ethernet': 'Ethernet',
  'usb': 'USB',
  'can': 'CAN',
  'rs232': 'RS-232',
  'rs485': 'RS-485',
  // Memory
  'ram': 'RAM',
  'flash': 'Flash-geheugen',
  'memory': 'Geheugen',
  'cache': 'Cache',
  // Identity
  'br id code': 'B&R ID-code',
  'b r id code': 'B&R ID-code',
  'article number': 'Artikelnummer',
  'order number': 'Bestelnummer',
  'revision': 'Revisie',
  'version': 'Versie',
  // Certification
  'ce marking': 'CE-markering',
  'ul certification': 'UL-certificering',
  'ul listed': 'UL-gecertificeerd',
  'atex': 'ATEX',
}

// eslint-disable-next-line react-refresh/only-export-components
export function localizeSpecKey(raw: string, lang: string): string {
  if (lang === 'nl') {
    const normalized = raw.toLowerCase().replace(/_/g, ' ')
    const nl = NL_SPEC_LABEL[normalized]
    if (nl) return nl
  }
  return formatKeyForDisplay(raw)
}

function localizeSpecValue(val: string, lang: string): string {
  if (lang === 'nl') {
    if (val === 'Yes') return 'Ja'
    if (val === 'No') return 'Nee'
  }
  return val
}

interface ProductAttributesProps {
  technical_attributes: TechnicalAttribute[]
  filterable_attributes: Record<string, string>
  compliance_attributes: Record<string, string>
}

/** Clean a spec value: strip excessive whitespace, HTML fragments, and long garbage strings */
function cleanValue(value: string): string {
  const v = value
    .replace(/<[^>]*>/g, '') // strip HTML
    .replace(/\s{2,}/g, ' ') // collapse whitespace
    .replace(/\n{2,}/g, '\n')
    .trim()
  // Skip values that are too long (likely scraping artifacts)
  if (v.length > 200) return v.substring(0, 200) + '…'
  return v
}

/** Filter out garbage/useless attributes */
function isUsefulAttribute(attr: { key: string; value: string }): boolean {
  const key = attr.key.toLowerCase()
  const val = cleanValue(attr.value)
  // Skip empty values
  if (!val || val === '-' || val === '—' || val === 'N/A') return false
  // Skip keys that are just version numbers or file paths
  if (key.includes('pdf') && key.includes('module')) return false
  if (val.includes('/en/downloads/')) return false
  // Skip overly long keys (scraping artifacts)
  if (attr.key.length > 80) return false
  return true
}

/** Format a raw key for display: replace underscores with spaces, title-case words. */
function formatKeyForDisplay(raw: string): string {
  return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Parse a `Record<string, string>` with dot-notation keys into grouped rows.
 *
 * Keys like `Certifications.Atex` become group "Certifications", display key "Atex".
 * Keys without a dot go into the "General" group.
 * Preserves insertion order within each group.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function parseGroupedAttributes(
  attrs: Record<string, string>,
): Map<string, Array<{ key: string; value: string }>> {
  const groups = new Map<string, Array<{ key: string; value: string }>>()

  for (const [rawKey, value] of Object.entries(attrs)) {
    const dotIndex = rawKey.indexOf('.')
    let group: string
    let displayKey: string

    if (dotIndex > 0) {
      group = formatKeyForDisplay(rawKey.substring(0, dotIndex))
      displayKey = formatKeyForDisplay(rawKey.substring(dotIndex + 1))
    } else {
      group = 'General'
      displayKey = formatKeyForDisplay(rawKey)
    }

    const row = { key: displayKey, value }
    if (!isUsefulAttribute(row)) continue

    if (!groups.has(group)) groups.set(group, [])
    groups.get(group)!.push(row)
  }

  return groups
}

function AttributeTable({
  rows,
  query = '',
}: {
  rows: Array<{ key: string; value: string; unit?: string }>
  query?: string
}) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const q = query.toLowerCase()
  const cleaned = rows
    .filter(isUsefulAttribute)
    .map(r => ({ ...r, value: cleanValue(r.value) }))
    .filter(r => !q || r.key.toLowerCase().includes(q) || r.value.toLowerCase().includes(q))

  if (cleaned.length === 0) {
    return <p className="text-sm text-neutral-600 italic">{t('shop.specs.noData')}</p>
  }

  return (
    <table className="w-full text-sm border-collapse">
      <tbody>
        {cleaned.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-neutral-50' : 'bg-white'}>
            <td className="py-2.5 px-3 text-neutral-700 font-medium w-2/5 align-top border-b border-neutral-100">
              {localizeSpecKey(row.key, lang)}
            </td>
            <td className="py-2.5 px-3 text-neutral-900 align-top border-b border-neutral-100">
              {localizeSpecValue(row.value, lang)}
              {row.unit && <span className="text-neutral-600 ml-1">{row.unit}</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function rowMatchesQuery(row: { key: string; value: string }, q: string): boolean {
  return row.key.toLowerCase().includes(q) || cleanValue(row.value).toLowerCase().includes(q)
}

/** Render grouped attribute sections: a header per group, then its attribute rows. */
function GroupedAttributeSections({
  groups,
  query = '',
}: {
  groups: Map<string, Array<{ key: string; value: string }>>
  query?: string
}) {
  const { i18n } = useTranslation()
  if (groups.size === 0) return null

  function localizeGroup(name: string): string {
    if (i18n.language === 'nl') return NL_GROUP[name] ?? name
    return name
  }

  const q = query.toLowerCase()

  return (
    <>
      {[...groups.entries()].map(([group, rows]) => {
        const useful = rows.filter(isUsefulAttribute)
        if (q && !useful.some(r => rowMatchesQuery(r, q))) return null
        return (
          <div key={group} className="mb-4 last:mb-0">
            {groups.size > 1 && (
              <h4 className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-2 px-3">
                {localizeGroup(group)}
              </h4>
            )}
            <AttributeTable rows={rows} query={query} />
          </div>
        )
      })}
    </>
  )
}

export default function ProductAttributes({
  technical_attributes,
  filterable_attributes,
  compliance_attributes,
}: ProductAttributesProps) {
  const { t, i18n } = useTranslation()
  const [query, setQuery] = useState('')

  // Group technical attributes by subgroup
  const techGroups = new Map<string, TechnicalAttribute[]>()
  function localizeGroup(name: string): string {
    if (i18n.language === 'nl') return NL_GROUP[name] ?? name
    return name
  }

  for (const attr of technical_attributes) {
    if (!isUsefulAttribute(attr)) continue
    const group = attr.subgroup || attr.group || 'General'
    if (!techGroups.has(group)) techGroups.set(group, [])
    techGroups.get(group)!.push(attr)
  }

  // Parse dot-notation keys into grouped sections
  const filterableGroups = parseGroupedAttributes(filterable_attributes ?? {})
  const complianceGroups = parseGroupedAttributes(compliance_attributes ?? {})

  const totalTechSpecs = [...techGroups.values()].reduce((sum, g) => sum + g.filter(isUsefulAttribute).length, 0)
  const totalFilterable = [...filterableGroups.values()].reduce((sum, rows) => sum + rows.length, 0)
  const totalCompliance = [...complianceGroups.values()].reduce((sum, rows) => sum + rows.length, 0)
  const totalRows = totalTechSpecs + totalFilterable + totalCompliance

  return (
    <div className="w-full">
      {totalRows > 10 && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-600" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('shop.specs.specsSearch')}
            className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-neutral-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      )}
      <Accordion type="multiple" defaultValue={['specs', 'filterable']} className="w-full">
        {totalTechSpecs > 0 && (
          <AccordionItem value="specs">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                {t('shop.specs.technical')}
                <span className="text-xs bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">{totalTechSpecs}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {[...techGroups.entries()].map(([group, attrs]) => {
                const q = query.toLowerCase()
                const useful = attrs.filter(isUsefulAttribute)
                if (q && !useful.some(r => rowMatchesQuery(r, q))) return null
                return (
                  <div key={group} className="mb-4 last:mb-0">
                    {techGroups.size > 1 && (
                      <h4 className="text-xs font-semibold text-neutral-600 uppercase tracking-wide mb-2 px-3">{localizeGroup(group)}</h4>
                    )}
                    <AttributeTable rows={attrs} query={query} />
                  </div>
                )
              })}
            </AccordionContent>
          </AccordionItem>
        )}

        {totalFilterable > 0 && (
          <AccordionItem value="filterable">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                {t('shop.specs.keyFeatures')}
                <span className="text-xs bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">{totalFilterable}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <GroupedAttributeSections groups={filterableGroups} query={query} />
            </AccordionContent>
          </AccordionItem>
        )}

        {totalCompliance > 0 && (
          <AccordionItem value="compliance">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                {t('shop.specs.compliance')}
                <span className="text-xs bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded-full">{totalCompliance}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <GroupedAttributeSections groups={complianceGroups} query={query} />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  )
}
